/**
 * Popover — variante ancrée (.popover--anchored)
 *
 * L'ancrage CSS (anchor-name / position-anchor) exige un nom d'ancre UNIQUE par
 * instance : si plusieurs popovers partagent le même nom, la spec retient le
 * dernier du DOM et tous s'ancrent au dernier bouton (cas typique : un menu
 * d'actions par ligne de tableau, tous ouverts au même endroit).
 *
 * initPopover() donne à chaque instance un nom d'ancre unique dérivé de l'id du
 * contenu (qui existe déjà, imposé par l'API popover native via popovertarget).
 * Le CSS lit ce nom via --ds-popover-anchor-name ; on ne fait que poser la
 * custom property sur le conteneur. Aucun effet sur le .popover classique
 * (:focus-within) ni sur une instance qui a déjà un nom explicite (style inline).
 *
 * Purement additif : sans ce JS (SSR, statique), le repli CSS --ds-popover-anchor
 * garde une seule instance fonctionnelle par page — comportement d'origine.
 */
export function initPopover(container = document) {
  const anchored = container.querySelectorAll('.popover--anchored');
  if (!anchored.length) return;

  // Détection de collision : plusieurs instances sans nom explicite = risque de
  // bug silencieux si ce JS ne tourne pas. On avertit, puis on corrige.
  const withoutExplicit = [];

  anchored.forEach((popover) => {
    // Respecter un nom déjà posé à la main (style inline / surcharge).
    if (popover.style.getPropertyValue('--ds-popover-anchor-name').trim()) return;

    // L'ancre se dérive de l'id du contenu (lien natif popovertarget ↔ id).
    const trigger = popover.querySelector('[popovertarget]');
    const targetId =
      trigger?.getAttribute('popovertarget') ||
      popover.querySelector('[popover][id]')?.id;

    if (!targetId) return; // pas un popover ancré exploitable, on laisse le CSS de repli

    withoutExplicit.push(popover);
    // Nom d'ancre valide (dashed-ident) unique et stable, dérivé de l'id.
    popover.style.setProperty('--ds-popover-anchor-name', `--ds-anchor-${targetId}`);
  });

  if (withoutExplicit.length > 1 && typeof console !== 'undefined') {
    // Avertissement utile si le JS a dû corriger plusieurs instances : en son
    // absence (rendu statique), elles se seraient toutes ancrées au même endroit.
    console.warn(
      `[ds-popover] ${withoutExplicit.length} popovers .popover--anchored sans ` +
        `--ds-popover-anchor-name explicite sur cette page. Noms d'ancre générés ` +
        `automatiquement depuis leur id. Sans ce JS (SSR/statique), ils se ` +
        `seraient tous ancrés au dernier bouton du DOM.`
    );
  }
}
